<template>
  <div class="questionnaire-list">
    <TableList
      :loading="loading"
      :listData="listData"
      :tableColumns="tableColumns"
      :actionBtn="actionBtn"
      :pagination="pagination"
      :defaultSort="defaultSort"
      @pagination-change="handlePaginationChange"
      @sort-change="handleSortChange"
    >
      <template #filter>
        <el-select
          v-model="filters.companyId"
          class="filter-select"
          filterable
          clearable
          :placeholder="$t(`${i18nPrefix}.placeholder.company`)"
        >
          <el-option
            v-for="item in companyOptions"
            :key="item.id"
            :label="item.i18nName"
            :style="{ paddingLeft: item.level * 15 + 'px' }"
            :value="item.id"
          />
        </el-select>
        <el-select
          v-model="filters.status"
          class="filter-select"
          clearable
          :placeholder="$t(`${i18nPrefix}.placeholder.status`)"
        >
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="$t(item.labelI18nKey)"
            :value="item.value"
          />
        </el-select>
      </template>
      <template #btn>
        <el-button class="create-btn" @click="handleCreate">{{ $t('common.btn.add') }}</el-button>
      </template>
      <template #scale="{ data }">
        {{ data || '-' }}
      </template>
      <template #hideHmRatings="{ data }">
        {{ $t(`${i18nPrefix}.hmRatings.${data ? 'private' : 'public'}`) }}
      </template>
      <template #status="{ row }">
        <el-switch
          v-model="row.status"
          active-color="#0096cc"
          inactive-color="#909399"
          :disabled="row.isInherited"
          @change="handleStatusChange(row)"
        />
      </template>
    </TableList>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { HM_FORM_STATUS, HM_QUESTIONNAIRE_STATUS } from '@/constant/questionnaire'
import { addI18nProperty } from '@/i18n'

export default {
  components: {
    TableList
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'admin.company.questionnaireManagement',
      listData: [],
      companyOptions: [],
      companyMap: {},
      filters: {
        companyId: null,
        status: null
      },
      order: null,
      isApplyingRouteQuery: false,
      isUpdatingRouteQuery: false,
      pagination: {
        page: 1,
        pageSize: 10,
        pageSizes: [10, 20, 50],
        total: 0,
        layout: 'total, sizes, prev, pager, next, jumper'
      }
    }
  },
  computed: {
    statusOptions () {
      return [
        { value: HM_FORM_STATUS.OPEN, labelI18nKey: `${this.i18nPrefix}.questionnaireStatus.open` },
        { value: HM_FORM_STATUS.CLOSED, labelI18nKey: `${this.i18nPrefix}.questionnaireStatus.closed` }
      ]
    },
    defaultSort () {
      return {
        prop: this.order ? 'status' : null,
        order: this.order === '-status' ? 'descending' : (this.order ? 'ascending' : null)
      }
    },
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.questionnaireName`),
          dataKey: 'nameI18n',
          width: '200px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.company`),
          dataKey: 'companyName',
          width: '124px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.scale`),
          dataKey: 'scaleScore',
          slotName: 'scale',
          align: 'center',
          width: '86px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.hmRatingsPrivate`),
          dataKey: 'hideHmRatings',
          slotName: 'hideHmRatings',
          width: '140px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'status',
          slotName: 'status',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '92px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.timesUsed`),
          dataKey: 'usedCount',
          align: 'center',
          width: '100px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.lastUpdate`),
          dataKey: 'updatedAt',
          width: '120px'
        }
      ]
    },
    actionBtn () {
      return {
        edit: {
          show: row => !row.isInherited,
          labelI18nKey: 'common.btn.edit',
          onclick: this.handleEdit
        },
        view: {
          show: row => row.isInherited,
          labelI18nKey: `${this.i18nPrefix}.btn.view`,
          onclick: this.handleView
        },
        copy: {
          show: true,
          labelI18nKey: `${this.i18nPrefix}.btn.copy`,
          onclick: this.handleCopy
        }
      }
    }
  },
  created () {
    this.applyRouteQuery()
    this.initData()
  },
  methods: {
    ...mapActions('questionnaire', [
      'searchHmQuestionnaires',
      'copyHmQuestionnaire',
      'updateHmQuestionnaireStatus'
    ]),
    ...mapActions('company', ['getCompanyChildrenTree']),
    async initData () {
      this.loading = true
      try {
        const companyTree = await this.getCompanyChildrenTree({ is_all: 1 })
        const companies = this.formatCompanyData(companyTree)
        this.companyMap = _.keyBy(companies || [], 'id')
        this.companyOptions = companies
        this.ensureCompanyFilterAvailable()
        await this.fetchList()
      } finally {
        this.loading = false
      }
    },
    formatDate (value) {
      return value ? moment(value).format('YYYY-MM-DD') : ''
    },
    formatCompanyName (company) {
      return _.chain(['i18nName', 'cn_name', 'name'])
        .map(key => _.get(company, key))
        .find(Boolean)
        .value() || ''
    },
    collectCompanyMap (data) {
      const companyMap = {}
      const addCompany = company => {
        if (company && company.id) {
          companyMap[company.id] = company
        }
      }
      addCompany(_.get(data, 'root'))
      addCompany(_.get(data, 'self'))
      addCompany(_.get(data, 'hr_self'))
      _.forEach(_.get(data, 'children', {}), children => {
        _.forEach(children, child => {
          addCompany(child)
        })
      })
      return companyMap
    },
    formatCompanyData (data) {
      const currentCompany = _.get(data, 'hr_self') || _.get(data, 'self')
      if (!currentCompany) {
        return []
      }
      const companyMap = this.collectCompanyMap(data)
      const companyList = []
      const visitedCompanyMap = {}
      let company = companyMap[currentCompany.id] || currentCompany
      while (company && !visitedCompanyMap[company.id]) {
        visitedCompanyMap[company.id] = true
        addI18nProperty(company, 'i18nName', 'cn_name', 'name')
        companyList.unshift(company)
        company = companyMap[company.parent_id]
      }
      return _.map(companyList, (item, index) => ({
        ...item,
        level: index + 1
      }))
    },
    ensureCompanyFilterAvailable () {
      if (this.filters.companyId && !_.has(this.companyMap, this.filters.companyId)) {
        this.isApplyingRouteQuery = true
        this.filters.companyId = null
        this.replaceRouteQuery()
        this.$nextTick(() => {
          this.isApplyingRouteQuery = false
        })
      }
    },
    normalizePositiveInteger (value, fallback) {
      const numberValue = Number(value)
      return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : fallback
    },
    normalizeQuestionnaireStatusQuery (status) {
      if (status === 'open') {
        return HM_FORM_STATUS.OPEN
      }
      if (status === 'closed') {
        return HM_FORM_STATUS.CLOSED
      }
      return null
    },
    normalizeCompanyQuery (companyId) {
      if (!companyId) {
        return null
      }
      const numberValue = Number(companyId)
      return Number.isInteger(numberValue) ? numberValue : null
    },
    normalizeListOrder (order) {
      return _.includes(['status', '-status'], order) ? order : '-status'
    },
    applyRouteQuery () {
      const query = this.$route.query || {}
      this.isApplyingRouteQuery = true
      this.filters = {
        companyId: this.normalizeCompanyQuery(query.company_id),
        status: this.normalizeQuestionnaireStatusQuery(query.status)
      }
      this.order = this.normalizeListOrder(query.order)
      this.pagination = {
        ...this.pagination,
        page: this.normalizePositiveInteger(query.page, 1),
        pageSize: this.normalizePositiveInteger(query.page_size, this.pagination.pageSize)
      }
      this.$nextTick(() => {
        this.isApplyingRouteQuery = false
      })
    },
    buildRouteQuery () {
      return _.omitBy({
        company_id: this.filters.companyId,
        status: _.isBoolean(this.filters.status) ? (this.filters.status ? 'open' : 'closed') : undefined,
        order: this.order || undefined,
        page: this.pagination.page,
        page_size: this.pagination.pageSize
      }, value => value === undefined || value === null || value === '')
    },
    replaceRouteQuery () {
      const query = this.buildRouteQuery()
      if (_.isEqual(this.$route.query, query)) {
        return
      }
      this.isUpdatingRouteQuery = true
      this.$router.replace({ path: this.$route.path, query }).catch(err => err).finally(() => {
        this.isUpdatingRouteQuery = false
      })
    },
    normalizeScale (scale) {
      if (!scale) {
        return null
      }
      return {
        id: scale.id,
        minScore: Number(scale.min_score),
        score: Number(scale.max_score),
        label: `${Number(scale.max_score)}`
      }
    },
    normalizeQuestionnaire (questionnaire) {
      const scale = this.normalizeScale(questionnaire.score_scale)
      const isInherited = Boolean(questionnaire.is_inherited)
      const company = this.companyMap[questionnaire.company_id]
      return addI18nProperty({
        id: questionnaire.id,
        name: questionnaire.name_zh || questionnaire.name_en,
        nameCn: questionnaire.name_zh,
        nameEn: questionnaire.name_en,
        companyId: questionnaire.company_id,
        companyName: this.formatCompanyName(company) || `${questionnaire.company_id || ''}`,
        scaleId: questionnaire.score_scale_id,
        scaleScore: scale ? scale.label : null,
        hideHmRatings: questionnaire.hm_privacy_enabled,
        status: questionnaire.status === 'enabled',
        usedCount: questionnaire.used_count || 0,
        locked: Boolean(questionnaire.is_structure_locked) || isInherited,
        isInherited,
        updatedAt: this.formatDate(questionnaire.updated_time),
        raw: questionnaire
      }, 'nameI18n', 'nameCn', 'nameEn')
    },
    buildSearchParams () {
      const status = this.filters.status
      return {
        include_inherited: true,
        page_num: this.pagination.page,
        page_size: this.pagination.pageSize,
        company_id: this.filters.companyId ? this.filters.companyId : undefined,
        status: _.isBoolean(status) ? (status ? HM_QUESTIONNAIRE_STATUS.ENABLED : HM_QUESTIONNAIRE_STATUS.DISABLED) : null,
        order: this.order || undefined
      }
    },
    async fetchList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      this.loading = true
      try {
        const resp = await this.searchHmQuestionnaires(this.buildSearchParams())
        this.listData = _.map(resp.data, item => this.normalizeQuestionnaire(item))
        this.pagination.total = resp.total
        this.pagination.page = resp.page_num
      } finally {
        this.loading = false
      }
    },
    handleCreate () {
      this.$router.push({ path: '/v-admin/questionnaire-management/questionnaire/add', query: this.$route.query })
    },
    handleEdit (row) {
      this.$router.push({ path: `/v-admin/questionnaire-management/questionnaire/edit/${row.id}`, query: this.$route.query })
    },
    handleView (row) {
      this.$router.push({ path: `/v-admin/questionnaire-management/questionnaire/view/${row.id}`, query: this.$route.query })
    },
    async handleCopy (row) {
      this.loading = true
      try {
        const resp = await this.copyHmQuestionnaire(row.id)
        const questionnaire = this.normalizeQuestionnaire(resp)
        this.$message.success(this.$t('common.message.optionSuccess'))
        this.$router.push({ path: `/v-admin/questionnaire-management/questionnaire/edit/${questionnaire.id}`, query: this.$route.query })
      } finally {
        this.loading = false
      }
    },
    async handleStatusChange (row) {
      const nextStatus = row.status
      this.loading = true
      try {
        const resp = await this.updateHmQuestionnaireStatus({ id: row.id, status: nextStatus })
        const questionnaire = this.normalizeQuestionnaire(resp)
        _.assign(row, {
          status: questionnaire.status,
          updatedAt: questionnaire.updatedAt
        })
        this.$message.success(this.$t('common.message.optionSuccess'))
        if (_.isBoolean(this.filters.status) && row.status !== this.filters.status) {
          await this.fetchList()
        }
      } catch (e) {
        row.status = !nextStatus
        this.$message.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handlePaginationChange (pagination) {
      this.pagination = pagination
      this.replaceRouteQuery()
      this.fetchList()
    },
    handleSortChange (prop, order) {
      this.order = `${order}${prop}`
      this.pagination.page = 1
      this.pagination.total = 0
      this.replaceRouteQuery()
      this.fetchList()
    }
  },
  watch: {
    '$route.query' () {
      if (this.isUpdatingRouteQuery) {
        return
      }
      this.applyRouteQuery()
      this.fetchList()
    },
    filters: {
      deep: true,
      handler () {
        if (this.isApplyingRouteQuery) {
          return
        }
        this.pagination.page = 1
        this.pagination.total = 0
        this.replaceRouteQuery()
        this.fetchList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-list {
  .filter-select {
    width: 150px;
  }
}
</style>
